// [Codex 2026-07-11] S134 cross-device run replay and recovery.
import { useEffect, useRef, type MutableRefObject } from "react";
import { getAccessToken } from "../../../lib/supabase";
import {
  readDurableQueueSnapshot,
  type DurableQueueSnapshotItem,
} from "../durable-queue-snapshot";

export interface DurableChatRun {
  id: string;
  status: string;
  metadata: Record<string, unknown>;
  provider?: string;
  updatedAt: string;
}

interface DurableRunRecoveryOptions {
  enabled: boolean;
  baseUrl: string;
  conversationId?: string;
  runId?: string | null;
  onActive: (run: DurableChatRun) => void;
  onUiEvent: (run: DurableChatRun, event: Record<string, unknown>) => void;
  onFinished: (run: DurableChatRun) => void;
  onRecoveryError: (run: DurableChatRun, message: string) => void;
  onQueueSnapshot?: (
    conversationId: string,
    items: DurableQueueSnapshotItem[],
  ) => void;
}

export function useDurableRunRecovery(options: DurableRunRecoveryOptions) {
  const callbacks = useRef(options);
  callbacks.current = options;

  useEffect(() => {
    if (!options.enabled || (!options.conversationId && !options.runId)) return;
    const controller = new AbortController();
    if (options.runId) {
      void recoverKnownRun(
        options.baseUrl,
        options.runId,
        controller.signal,
        callbacks,
      );
    } else if (options.conversationId) {
      void recoverConversationRun(
        options.baseUrl,
        options.conversationId,
        controller.signal,
        callbacks,
      );
    }
    return () => controller.abort();
  }, [options.baseUrl, options.conversationId, options.enabled, options.runId]);
}

async function recoverKnownRun(
  baseUrl: string,
  runId: string,
  signal: AbortSignal,
  callbacks: MutableRefObject<DurableRunRecoveryOptions>,
) {
  try {
    const headers = await authHeaders();
    const response = await fetch(
      `${baseUrl}/api/chat/runs/${encodeURIComponent(runId)}`,
      { headers, signal },
    );
    if (!response.ok || signal.aborted) return;
    const body = await response.json();
    const run = body.run as DurableChatRun | undefined;
    if (!run) return;
    callbacks.current.onActive(run);
    if (["blocked", "completed", "failed", "cancelled"].includes(run.status)) {
      callbacks.current.onFinished(run);
      return;
    }
    await attachRunStream(baseUrl, run, headers, signal, callbacks);
  } catch (error) {
    if (!signal.aborted)
      console.warn("[durable-chat] Run attach failed:", error);
  }
}

async function recoverConversationRun(
  baseUrl: string,
  conversationId: string,
  signal: AbortSignal,
  callbacks: MutableRefObject<DurableRunRecoveryOptions>,
) {
  try {
    const headers = await authHeaders();
    const projectionResponse = await fetch(
      `${baseUrl}/api/chat/conversations/${encodeURIComponent(conversationId)}/runs`,
      { headers, signal },
    );
    if (!projectionResponse.ok || signal.aborted) return;
    const body = await projectionResponse.json();
    const projection = body.projection ?? {};
    callbacks.current.onQueueSnapshot?.(
      conversationId,
      readDurableQueueSnapshot(projection),
    );
    const activeRun = projection.activeTask as DurableChatRun | undefined;
    if (!activeRun) {
      const latest = (projection.tasks?.[0] ?? null) as DurableChatRun | null;
      if (latest?.status === "blocked") {
        callbacks.current.onRecoveryError(
          latest,
          stringValue(latest.metadata?.message) ??
            "Harper stopped before the run finished. Your work is saved; ask Harper to continue.",
        );
      }
      return;
    }

    callbacks.current.onActive(activeRun);
    await attachRunStream(baseUrl, activeRun, headers, signal, callbacks);
  } catch (error) {
    if (signal.aborted) return;
    console.warn("[durable-chat] Run recovery failed:", error);
  }
}

async function attachRunStream(
  baseUrl: string,
  run: DurableChatRun,
  headers: Headers,
  signal: AbortSignal,
  callbacks: MutableRefObject<DurableRunRecoveryOptions>,
) {
  const streamResponse = await fetch(
    `${baseUrl}/api/chat/runs/${encodeURIComponent(run.id)}/stream`,
    { headers, signal },
  );
  if (!streamResponse.ok || !streamResponse.body) return;
  await readRunStream(streamResponse.body, run, signal, callbacks);
}

async function readRunStream(
  stream: ReadableStream<Uint8Array>,
  activeRun: DurableChatRun,
  signal: AbortSignal,
  callbacks: MutableRefObject<DurableRunRecoveryOptions>,
) {
  const reader = stream.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (!signal.aborted) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() ?? "";
    for (const frame of frames) {
      const event = parseSseEvent(frame);
      if (!event) continue;
      if (event.type === "data-run-state") {
        const run = (event.data ?? activeRun) as DurableChatRun;
        if (run.status === "blocked" || run.status === "failed") {
          callbacks.current.onRecoveryError(
            run,
            stringValue(run.metadata?.message) ??
              "Harper could not finish this run.",
          );
        }
        callbacks.current.onFinished(run);
      } else {
        callbacks.current.onUiEvent(activeRun, event);
      }
    }
  }
}

async function authHeaders(): Promise<Headers> {
  const headers = new Headers({ "Content-Type": "application/json" });
  const token = await getAccessToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return headers;
}

function parseSseEvent(frame: string): Record<string, any> | null {
  const data = frame
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.replace(/^data:\s?/, ""))
    .join("\n")
    .trim();
  if (!data || data === "[DONE]") return null;
  try {
    const parsed = JSON.parse(data);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}
